/**
 * Texture layer (design system, FOUNDATION — v5 "PIXEL SWEETHEART").
 *
 * <GrainOverlay/>   — a whisper of warm grain across the viewport. One per
 *                     surface tree (the shell mounts it; standalone screens
 *                     like the landing mount their own). pointer-events:none,
 *                     top layer, GPU-only drift; freezes into static grain
 *                     under prefers-reduced-motion (global CSS rule).
 * <AmbientBackground/> — the cute-warm backdrop: soft candy glows drifting
 *                     slowly, a faint conic wash, a gentle vignette — and the
 *                     v5 signature: a handful of tiny PIXEL HEARTS and
 *                     sparkles floating up like bubbles in lemonade. Subtle
 *                     (low opacity), GPU-cheap (transform/opacity only),
 *                     paused under reduced motion. Cosy, never busy.
 *
 * Both are pure cosmetics — no ARIA surface, nothing focusable. Class names
 * unchanged (compat); the pixel layer is additive.
 */
import { PixelHeart, PixelHeartCracked, PixelSparkle } from "./pixel.js";

/** Subtle warm grain — mount once per screen tree, above everything. */
export function GrainOverlay() {
  return <div className="fx-grain" aria-hidden="true" data-testid="fx-grain" />;
}

/* the floating pixel bits — positions/timings live in humble.css
   (.fx-ambient__px--1…7); a mix of hearts, one cracked heart (the brand
   wink) and sparkles, all barely-there */
const PIXEL_BITS = [
  { key: 1, El: PixelHeart, size: 14 },
  { key: 2, El: PixelSparkle, size: 10 },
  { key: 3, El: PixelHeart, size: 10 },
  { key: 4, El: PixelHeartCracked, size: 16 },
  { key: 5, El: PixelSparkle, size: 8 },
  { key: 6, El: PixelHeart, size: 18 },
  { key: 7, El: PixelSparkle, size: 12 },
] as const;

/** Slow candy glows + floating pixel hearts + vignette, behind content. */
export function AmbientBackground() {
  return (
    <div className="fx-ambient" aria-hidden="true" data-testid="fx-ambient">
      <div className="fx-ambient__mesh" />
      <span className="fx-ambient__orb fx-ambient__orb--accent" />
      <span className="fx-ambient__orb fx-ambient__orb--sr" />
      <span className="fx-ambient__orb fx-ambient__orb--like" />
      {PIXEL_BITS.map(({ key, El, size }) => (
        <span key={key} className={`fx-ambient__px fx-ambient__px--${key}`}>
          <El size={size} />
        </span>
      ))}
      <div className="fx-ambient__vignette" />
    </div>
  );
}
